<template>
    <div class="waiver">
        <div class="waiver__info-row">
            <div class="waiver__info">
                <strong>{{ waiver.name }}</strong>
                <small>
                    {{ waiver.requiredFormCount }}
                    {{ langPluralFilter(waiver.requiredFormCount, 'WAIVERS.WAIVER_COUNT') }}
                </small>
                <small v-if="isRedeemed"
                       class="text-success">
                    <i class="fa-solid fa-check-circle"></i>
                    {{ l('WAIVERS.REDEEMED_AT') }} {{ relativeTimeFilter(waiver.redeemedAt) }}
                </small>
                <small v-else-if="isComplete"
                       class="text-success">
                    <i class="fa-solid fa-check-circle"></i>
                    {{ l('WAIVERS.COMPLETED_AT') }} {{ relativeTimeFilter(waiver.completedAt) }}
                </small>
            </div>

            <div class="waiver__btns">
                <SquareBtn color="info"
                           @click="openCode">
                    <i class="fa-solid fa-qrcode"></i>
                    {{ l('WAIVERS.OPEN_CODE_BTN') }}
                </SquareBtn>

                <SquareBtn v-if="!isComplete"
                           color="primary"
                           :loading="markingCompleted"
                           @click="markCompleted">
                    <i class="fa-solid fa-check-circle"></i>
                    {{ l('WAIVERS.COMPLETE_BTN') }}
                </SquareBtn>
            </div>
        </div>

        <!-- Clickable box to show the list of completed waiver forms: -->
        <div v-if="waiver.waiverForms.length > 0"
             class="waiver__forms"
             :class="{'waiver_forms--expanded': formsVisible}">
            <!-- Toggle to show/hide waiver forms -->
            <div class="waiver__forms-toggle"
                 @click="formsVisible = !formsVisible">
                <div class="flex--grow">
                    {{ l('WAIVERS.FORMS__HEADING') }}
                </div>
                <span v-if="formsVisible">
                    {{ l('WAIVERS.FORMS__HIDE_BTN') }}
                    <i class="fa-solid fa-chevron-up"></i>
                </span>
                <span v-else>
                    {{ l('WAIVERS.FORMS__SHOW_BTN') }}
                    <i class="fa-solid fa-chevron-down"></i>
                </span>
            </div>
            <!-- List of completed waiver forms. Toggled by the above line. -->
            <template v-if="formsVisible">
                <template v-for="form in waiver.waiverForms"
                          :key="`waiver-form-${form.ID}`">
                    <div class="waiver__form"
                         :class="{'waiver__form--not-attending': !form.attending}">
                        <div class="waiver__form__name-container">
                            <div class="waiver__form__name">
                                {{ form.firstName }} {{ form.lastName }}
                                <div class="waiver__form__status">
                                    <template v-if="form.age">
                                        <span>{{ l('WAIVERS.AGE') }}:</span> {{ form.age }}&nbsp;
                                    </template>
                                    <template v-if="form.guardian">
                                        <span>{{ l('WAIVERS.TYPE') }}:</span> {{ l('WAIVERS.GUARDIAN') }},
                                        {{ l(form.attending ? 'WAIVERS.ATTENDING' : 'WAIVERS.NOT_ATTENDING') }}
                                    </template>
                                </div>
                            </div>
                        </div>

                        <RoundBtn color="danger"
                                  size="sm"
                                  icon="fa-solid fa-trash"
                                  :loading="!!removingForms[form.ID]"
                                  :disabled="waiver.isRedeemed()"
                                  filled
                                  @click="removeForm(form)" />
                    </div>

                    <template v-if="form.minors.length > 0">
                        <div v-for="minor in form.minors"
                             :key="`waiver-form-minor${minor.ID}`"
                             class="waiver__form waiver__form--minor">
                            <div class="waiver__form__name">
                                {{ minor.firstName }} {{ minor.lastName }}
                                <div class="waiver__form__status">
                                    <template v-if="minor.age">
                                        <span>{{ l('WAIVERS.AGE') }}:</span> {{ minor.age }}&nbsp;
                                    </template>
                                    <span>{{ l('WAIVERS.TYPE') }}:</span> {{ l('WAIVERS.MINOR') }}
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import RoundBtn from '@/vue/components/RoundBtn';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { updateAngularUi } from '@/functions/angular';

export default {
    components: { RoundBtn, SquareBtn },

    props: {
        waiver: {
            type: Object,
            required: true,
        },
    },
    emits: [
        'formRemoved',
    ],

    data() {
        return {
            markingCompleted: false,

            formsVisible: false,
            removingForms: {},
        };
    },

    computed: {
        isComplete() {
            return this.waiver.isComplete();
        },

        isRedeemed() {
            return this.waiver.isRedeemed();
        },

        waiverLink() {
            return this.waiver.getWaiverLink(container.appConfig);
        },
    },

    methods: {
        openCode() {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.WAIVER_CODE,
                {
                    waiver: this.waiver,
                },
            );
        },

        markCompleted() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.COMPLETE_WAIVER,
                    {
                        waiver: this.waiver,
                    },
                )
                .then(() => {
                })
                .catch(suppressModalCancelledErrorOrReThrow)
                .finally(() => {
                    updateAngularUi();
                });
        },

        /**
         * @param {WaiverForm} form
         */
        removeForm(form) {
            const confirmProps = {
                cancelColor: 'default',
                cancelLabel: 'WAIVERS.REMOVE_WAIVER_CANCEL_BTN',
                okColor: 'primary',
                okLabel: 'WAIVERS.REMOVE_WAIVER_CONFIRM_BTN',
                modalSize: 'sm',
            };
            let confirmPromise;
            if (form.guardian) {
                confirmPromise = container.dialogService.confirm(
                    'WAIVERS.REMOVE_GUARDIAN__PROMPT',
                    {
                        title: 'WAIVERS.REMOVE_GUARDIAN__TITLE',
                        ...confirmProps,
                    },
                );
            } else {
                confirmPromise = container.dialogService.confirm(
                    'WAIVERS.REMOVE_WAIVER__PROMPT',
                    {
                        title: 'WAIVERS.REMOVE_WAIVER__TITLE',
                        ...confirmProps,
                    },
                );
            }

            confirmPromise
                .then((result) => {
                    if (result) {
                        this.removingForms[form.ID] = true;
                        container.waiversService
                            .removeWaiverForm(form.ID)
                            .then((removed) => {
                                if (removed) {
                                    this.waiver.removeForm(form);
                                    this.$emit('formRemoved');
                                }
                            })
                            .finally(() => {
                                delete this.removingForms[form.ID];
                            });
                    }
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.waiver {
    padding: 13px 11px;
    border-radius: @border-radius-base;

    // Row containing the name of the waiver and the buttons for the waiver.
    // Modified for desktop and mobile below.
    &__info-row {
        display: flex;
    }

    // Name of waivers + waiver count + completed at date
    &__info {
        flex-grow: 1;
        flex-shrink: 1;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        grid-gap: 2px;
        padding-left: 2px;
    }

    // Show QR Code, Mark Complete etc. button container
    &__btns {
        display: flex;
        grid-gap: @gutter-width / 2;
    }

    // Container for the completed waiver forms.
    // Each child element is a row with the name of somebody who has signed the form,
    // or a minor belonging to one of those forms.
    &__forms {
        background: rgba(0, 0, 0, .3);
        border-radius: @border-radius-base;
        margin-top: @gutter-width;
    }

    // Element to expand/collapse the list of completed forms.
    &__forms-toggle {
        padding: 10px;
        background: @nav-blue;
        border-radius: @border-radius-base;
        display: flex;
        grid-gap: @gutter-width;
        align-items: center;
        font-weight: bold;
        font-size: 13px;
        cursor: pointer;

        > span {
            color: @dark-grey;
        }
    }

    &.waiver-forms--expanded {
        .waiver__forms-toggle {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    &__form {
        padding: 10px 8px 10px 10px;
        display: flex;
        grid-gap: @gutter-width;
        align-items: center;
        border-top: 1px @mid-blue solid;

        &__name-container {
            flex-grow: 1;
        }

        &__name {
            flex-grow: 1;
        }

        &__status {
            color: @dt-light-grey;
            font-size: @font-size-small;
            > span {
                color: @dark-grey;
            }
        }

        &--not-attending {
            .waiver__form__name,
            .waiver__form__status {
                color: @dt-light-grey;
                opacity: .6;
            }
        }
    }
}

/* When the component is included in the modal that is launched the change modal. */
.modl-body .waiver__form__name,
.modl-body .waiver__form__status {
    color: @dt-dark-grey;
}
.modl-body .waiver__form--not-attending {
    .waiver__form__name,
    .waiver__form__status {
        opacity: .7;
    }
}

@media (max-width: @mobile-max) {
    .waiver {
        padding: 10px;

        &__info-row {
            flex-direction: column;
            grid-gap: 8px;
        }

        &__info {
            padding: 0 3px;
        }

        &__btns {
            display: flex;

            .btn {
                flex-grow: 1;
                flex-shrink: 1;
            }
        }

        &__forms {
            margin-top: 8px;
        }

        &__form {
            padding: 10px;

            &__status {
                font-size: 13px;
                margin-top: 1px;
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .waiver {
        &__info-row {
            align-items: center;
            justify-content: center;
            grid-gap: @half-gutter-width;
        }

        &__form {
            &__name-container {
                display: flex;
            }

            &__name {
                flex-grow: 1;
                flex-shrink: 1;
            }
        }
    }
}
</style>
